import { useAuthStore } from "@/features/authentication";
import { usersApi } from "@/shared/services";
import type { ParsedToken } from "firebase/auth";
import { useEffect } from "react";
import { useAccountStoreShallow } from "../store/account.store";

export const useSetupIntercom = () => {
  const claims = useAuthStore((s) => s.claims);
  const { planType, _id } = useAccountStoreShallow((s) => ({
    planType: s.planType,
    _id: s._id,
  }));

  useEffect(() => {
    if (claims && _id) {
      identifyIntercom(claims, planType);
    }
  }, [claims, _id, planType]);
};

const identifyIntercom = async (
  claims: ParsedToken,
  planType: "trial" | "starter" | "pro" | "team",
): Promise<void> => {
  const userTypeMap: Record<string, string> = {
    "creator lite": "creator_lite",
    creatorLite: "creator_lite",
    orgAdmin: "creator",
    creator: "creator",
    administrator: "admin",
    "storydoc admin": "admin",
  };

  const role = userTypeMap[claims.userType as string] || "user";

  // Identity verification (pentest CWE-285): the server signs a JWT proving the
  // authenticated user's identity so the Messenger can't be booted as another
  // user from the console. Intercom deprecated HMAC identity verification in
  // favour of JWTs, so we boot with `intercom_user_jwt` — the token carries the
  // verified `user_id`/`email`, so those must NOT be passed as plain boot params
  // (that was the original vulnerability). If the request fails we boot without
  // it, so the widget still loads (Intercom only rejects unverified boots once
  // JWT enforcement is enabled in the dashboard).
  let userJwt: string | undefined;
  try {
    ({ userJwt } = await usersApi.getIntercomHash());
  } catch (error) {
    console.error("Failed to fetch Intercom identity token", error);
  }

  window.Intercom("boot", {
    api_base: "https://api-iam.intercom.io",
    app_id: "w29p8yz8",
    intercom_user_jwt: userJwt,
    name: claims.name,
    plan_sd: planType,
    role_sd: role,
    hide_default_launcher: true,
    horizontal_padding: 40, // 68 + 24px offset so chat window clears the Help FAB
  });
};
